'use client';

import type { ComponentType, ReactNode } from 'react';
import { AlertCircle, Check } from 'lucide-react';

/** Same field styling as the redesigned auth forms (auth/components/AuthFields.tsx). */
export const inputClass = (hasError?: boolean) =>
  `block h-11 w-full rounded-xl border bg-white px-4 text-base text-ink placeholder:text-gray-500 transition-[border-color,box-shadow] focus:outline-none focus:ring-4 ${
    hasError ? 'border-heart-600 focus:border-heart-600 focus:ring-heart-600/15' : 'border-paper-rule hover:border-gray-300 focus:border-ink focus:ring-ink/10'
  }`;

export function Field({
  id,
  label,
  optional,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 flex items-baseline gap-2 text-sm font-semibold text-ink">
        {label}
        {optional && <span className="text-xs font-medium text-gray-500">Optional</span>}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="mt-1.5 flex items-center gap-1.5 text-sm text-heart-700">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-sm text-gray-600">{hint}</p>
      ) : null}
    </div>
  );
}

export type StepId = 'card' | 'recipients' | 'settings' | 'envelope';

export const STEPS: { id: StepId; label: string; short: string }[] = [
  { id: 'card', label: 'Designs & details', short: 'Designs' },
  { id: 'recipients', label: 'People', short: 'People' },
  { id: 'settings', label: 'Extras', short: 'Extras' },
  { id: 'envelope', label: 'Envelope & create', short: 'Envelope' },
];

export function Stepper({ current, onSelect }: { current: StepId; onSelect: (id: StepId) => void }) {
  const currentIndex = STEPS.findIndex((s) => s.id === current);
  return (
    <nav aria-label="Order steps">
      <ol className="flex items-center gap-2 sm:gap-3">
        {STEPS.map((step, i) => {
          const done = i < currentIndex;
          const active = i === currentIndex;
          return (
            <li key={step.id} className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => onSelect(step.id)}
                aria-current={active ? 'step' : undefined}
                className="group flex min-w-0 items-center gap-2.5 rounded-full py-1 pr-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-ink text-white'
                      : done
                        ? 'bg-sage-600 text-white'
                        : 'border border-paper-rule bg-white text-gray-600 group-hover:border-gray-300'
                  }`}
                >
                  {done ? <Check className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" /> : i + 1}
                </span>
                <span className={`hidden truncate text-sm font-semibold md:inline ${active ? 'text-ink' : 'text-gray-600 group-hover:text-ink'}`}>
                  {step.label}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <span className={`h-px min-w-[0.75rem] flex-1 ${i < currentIndex ? 'bg-sage-600' : 'bg-paper-rule'}`} aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>
      <p className="mt-3 text-sm text-gray-600 md:hidden">
        Step {currentIndex + 1} of {STEPS.length}: <span className="font-semibold text-ink">{STEPS[currentIndex].label}</span>
      </p>
    </nav>
  );
}

/** White panel used for each section of a step. */
export function Panel({
  title,
  description,
  aside,
  children,
  id,
}: {
  title: string;
  description?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="rounded-2xl border border-paper-rule bg-white p-5 shadow-card sm:p-7">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl font-medium text-ink sm:text-2xl">{title}</h2>
          {description && <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-600 sm:text-base">{description}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full border border-paper-rule bg-paper p-1">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
              active ? 'bg-white text-ink shadow-sm ring-1 ring-paper-rule' : 'text-gray-600 hover:text-ink'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function Switch({ checked, onChange, label, name }: { checked: boolean; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; label: string; name: string }) {
  return (
    <label className="relative inline-flex shrink-0 cursor-pointer items-center">
      <input type="checkbox" name={name} checked={checked} onChange={onChange} className="peer sr-only" aria-label={label} />
      <span className="h-6 w-11 rounded-full bg-gray-300 transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-ink peer-checked:after:translate-x-5 peer-focus-visible:ring-4 peer-focus-visible:ring-ink/15" />
    </label>
  );
}

/** Option card for an extra (confetti, music, logo): icon, copy, control, and details when on. */
export function ExtraCard({
  icon: Icon,
  tone,
  title,
  body,
  on,
  control,
  children,
  id,
}: {
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  tone: 'heart' | 'marigold' | 'sage';
  title: string;
  body: string;
  on: boolean;
  control: ReactNode;
  children?: ReactNode;
  id?: string;
}) {
  const tones = {
    heart: 'bg-heart-50 text-heart-600',
    marigold: 'bg-marigold-100 text-ink',
    sage: 'bg-sage-50 text-sage-700',
  };
  return (
    <div id={id} className={`flex flex-col rounded-2xl border p-5 transition-colors ${on ? 'border-ink/25 bg-white shadow-card' : 'border-paper-rule bg-paper/60'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3.5">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tones[tone]}`}>
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-ink">{title}</h3>
            <p className="mt-0.5 text-sm leading-relaxed text-gray-600">{body}</p>
          </div>
        </div>
        {control}
      </div>
      {children && <div className="mt-4 border-t border-paper-rule pt-4">{children}</div>}
    </div>
  );
}
